import React, { useState } from 'react';
import { Ticket, TicketStatus, OrderApi } from '@/lib/OrderApi';
import { revealValidationSummary } from '@/components/customer-details-form/validationSummaryReveal';
import { CustomerDetailsForm } from '@/components/customer-details-form/CustomerDetailsForm';
import { OrderedList, FormComponent } from '@/lib/TicketApi';
import * as m from "@/paraglide/messages.js"

/**
 * FAS-1188 — the API refuses a claim on a ticket that is no longer claimable with a stable reason
 * code rather than a sentence, so the visitor reads this in their own language instead of the
 * English domain message. Anything unrecognised (or a transport failure) falls back to the generic
 * line. Mirrors messageForReason in ReturnTicketModal.
 */
function messageForReason(reason: string | null | undefined): string {
    switch (reason) {
        case 'ticket_not_claimable':
            return m.ticket_claim_error_not_claimable();
        case 'ticket_not_found':
            return m.ticket_claim_error_gone();
        default:
            return m.ticket_claim_error_generic();
    }
}

interface ClaimTicketProps {
  orderId: string;
  ticket: Ticket;
  type: "self" | "shared";
  answers: Record<string, object[] | string[]>;
  onSuccess: () => void;
}

const ClaimTicket: React.FC<ClaimTicketProps> = ({ orderId, ticket, type, answers, onSuccess }) => {

    const [formState, setFormState] = useState<Record<string, object[] | string[]>>(type === "self" ? answers : {});
    const [formValid, setFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});

    function hasClaimForm(t: Ticket): t is Ticket & { claimForm: unknown } {
        return 'claimForm' in t;
    }

    if(ticket.ticketStatus !== TicketStatus.claimable) {
        return null;
    }

    if (!hasClaimForm(ticket)) {
        return null;
    }

    const claimTicket = async () => {
        if(!formValid) {
            return;
        }

        setLoading(true);
        setErrors({}); // Clear previous errors
        try {
            const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);
            const result = await ticketApi.selfClaimTicket(orderId, ticket.ticketId, formState);
            if (!result.success) {
                // A rejection of the claim itself has no form field to sit under, so it goes in
                // under `general` — the key CustomerDetailsForm renders as an unattributed line.
                // Before this it arrived as an empty error bag and the visitor saw nothing at all.
                setErrors(
                    Object.keys(result.errors || {}).length > 0
                        ? result.errors
                        : { general: [messageForReason(result.reason)] },
                );
                setLoading(false);
                return;
            }
            onSuccess();
        } catch (error) {
            // Without this the request failing outright (offline, 500) left the visitor staring
            // at an unchanged form with no indication anything had happened at all.
            console.error(error);
            setErrors({ general: [m.form_something_went_wrong()] });
        } finally {
            setLoading(false);
        }
    }
    
  return (
    <div className="flex flex-col gap-4">
      <p className="text-center text-sm opacity-70">
        {m.ticket_share_claim_ticket()}
      </p>
      <CustomerDetailsForm
        form={ticket.claimForm as OrderedList<FormComponent>}
        answers={formState}
        errors={errors}
        setAnswers={(answers) => setFormState(answers as Record<string, object[] | string[]>)}
        setFormValid={setFormValid}
      />
      {/* The button stops taking pointer events once it is disabled, so the wrapper is what hears
          the visitor reach for it — hovering, or tapping on a phone, where there is no hover. */}
      <div onMouseEnter={revealValidationSummary} onClick={revealValidationSummary}>
        <button className="bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md disabled:opacity-50 disabled:pointer-events-none" disabled={!formValid || loading} onClick={claimTicket} >
          {loading ? m.claiming_ticket() : m.ticket_share_claim_ticket()}
        </button>
      </div>
    </div>
  );
};

export default ClaimTicket;
